import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserScope } from '../contexts/UserScope';
import { useBalloonEventsData } from '../contexts/EventData';

/**
 * Bridges event selection to the user-scope profile pane. Lives inside
 * `EventDataProvider` (which `UserScopeProvider` wraps), so it can read both
 * contexts. Responsibilities:
 *  - When an event becomes selected/opened, close the profile pane
 *    synchronously (before paint, so the event-details pane never flashes
 *    behind it). Closing the event does NOT reopen the pane — it stays closed
 *    until the user reopens it via "View profile".
 *  - When an event closes while still scoped, restore the address bar to the
 *    scope URL. Opening an event mutates the URL via `history.replaceState`
 *    (bypassing react-router), so without this the bar would stay stuck on
 *    `/event/...` after closing even though react-router is still on
 *    `/user/:slug`.
 */
export function UserScopeEventSync(): null {
    const { slug, closeProfilePane } = useUserScope();
    const { selectedEvent, openedEvent } = useBalloonEventsData();
    const location = useLocation();
    const active = Boolean(selectedEvent || openedEvent);
    const wasActiveRef = useRef(false);

    useLayoutEffect(() => {
        if (active && !wasActiveRef.current) {
            closeProfilePane();
        } else if (!active && wasActiveRef.current && slug) {
            // Event just closed while scoped — re-sync the address bar with the
            // (unchanged) react-router scope location.
            window.history.replaceState(null, '', location.pathname + location.search);
        }
        wasActiveRef.current = active;
    }, [active, slug, closeProfilePane, location.pathname, location.search]);

    return null;
}

export default UserScopeEventSync;
